import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';

import { ProjectOverviewService } from './services/project-overview.service';
import { NewRouteService } from '../new-route/services/new-route.service';
import { ConfigService } from '../../utils/config-service';
import { MapsService } from '../maps';

import { Project } from '../domain/models/project';
import { RouteImport } from '../domain/models/route-import';
import { Alert } from '../domain/models/alert';
import { TotalUserQuota } from '../domain/models/TotalUserQuota';
import { Schedule } from '../domain/models/schedule';
import { Coords } from '../domain/models/coords';
import { RouteDraft } from '../domain/models/routeDraft';
import { Position } from '../domain/models/position';

import { Ordering } from '../domain/enums/ordering';
import { OverviewType } from '../domain/enums/overviewType';
import { JobStatus } from '../domain/enums/jobStatus';
import { RouteImportType } from '../domain/enums/routeImportType';
import { NewRouteViewModel } from '../new-route/new-route.viewModel';
import { EditRouteViewModel } from '../new-route/edit-route.viewModel';
import { ProjectOverviewViewModel, ProjectJobOverviewViewModel } from './viewModels/project-overview.viewModel';
import { CalculateQuotaRequest, CalculateQuotaResponse } from '../domain/models/calculateQuota';

import { tryParseInt } from '../../utils/try-parse';
import { sleep } from '../../utils/sleep';
import GuidService from '../../utils/guid-generator';
import * as _ from 'lodash';
import * as moment from 'moment/moment';
import { isNullOrUndefined } from 'util';

@Component({
  selector: 'app-project-overview',
  templateUrl: './project-overview.component.html',
  styleUrls: ['./project-overview.component.scss']
})
export class ProjectOverviewComponent implements OnInit {
  @ViewChild('searchBox') searchBox: ElementRef;

  private loadingProjects: boolean;
  private readingCsv: boolean;
  private validatingCsv: boolean;
  private createInProgress: boolean;
  private maxAllowedErrorRoutes: number;
  private deleteShown: boolean;
  private sharePopupShown: boolean;
  private stopSharingPopupShown: boolean;
  private stopShown: boolean;
  private createProjectShown: boolean;
  private routeImportShown: boolean;
  private alertsShown: boolean;

  private deleteRouteShown: boolean;
  private stopRouteShown: boolean;

  private expandedProjects: Array<string>;
  private userId: string;
  private alerts: Alert[];

  private selectedRoute: ProjectJobOverviewViewModel;
  private activeOverviewType: OverviewType;
  private searchboxActive: boolean;
  private fileReader: FileReader;
  private maxRouteUploadDeviationPercent: number;

  public searchTerm: string;
  public projects: Array<ProjectOverviewViewModel>;
  public currentProjects: Array<ProjectOverviewViewModel>;
  public filteredProjects: Array<ProjectOverviewViewModel>;

  public archivedProjects: Array<ProjectOverviewViewModel>;
  public selectedProject: ProjectOverviewViewModel;
  public selectedShareLink: string;
  public addedRoutes: RouteImport[];
  public availableRoutes: RouteImport[];
  public readonly routeImportType: RouteImportType;
  public maxQuota: number;

  public csvLinesToProcess: number;
  public currentCsvLineNumber: number;
  public csvLinesWithInsufficientQuota: number;

  public sortOrder: any = {
    Date: Ordering.CreatedDesc,
    Ascending: Ordering.AlphaAsc,
    Descending: Ordering.AlphaDesc
  };

  constructor(
    private router: Router,
    private projectOverviewService: ProjectOverviewService,
    private mapsService: MapsService,
    private newRouteService: NewRouteService,
    configService: ConfigService) {
    this.routeImportType = RouteImportType.Project;
    this.maxRouteUploadDeviationPercent = parseInt(configService.getEnv('maxRouteUploadDeviationPercent'), 10);
  }

  ngOnInit() {
    this.projects = [];
    this.expandedProjects = [];
    this.addedRoutes = [];
    this.availableRoutes = [];

    this.stopShown = false;
    this.alertsShown = false;

    this.userId = this.projectOverviewService.getUserId();

    this.activateMy();

    this.fileReader = new FileReader();
    this.loadProjects();
    this.loadMaxQuota();
    this.loadAlertsForUser();
  }

  private loadMaxQuota(): void {
    this.projectOverviewService.getMaxQuota()
      .subscribe((userQuota: TotalUserQuota) => {
        this.maxQuota = userQuota.totalQuota;
      });
  }

  private getProjectsUsedQuota(projects: ProjectOverviewViewModel[]): ProjectOverviewViewModel[] {
    projects.forEach(project => {
      project.jobs.forEach(job => {
        project.usedQuota += !isNullOrUndefined(job.cost) ? job.cost : 0;
      });
    });
    return projects;
  }

  private loadAlertsForUser(): void {
    this.projectOverviewService.getAlertsForUser()
      .subscribe((alerts: Alert[]) => {
        this.alerts = alerts;
      });
  }

  private loadProjects(): void {
    this.loadingProjects = true;
    this.projectOverviewService.getProjects()
      .subscribe((result: Array<ProjectOverviewViewModel>) => {
        const projectsWithCsvErrors = this.projects.filter(p => this.csvUploadFailed(p) || this.hasFailedCsvLines(p));
        this.projects = this.getProjectsUsedQuota(result);
        this.reloadCsvErrorsInCurrentProjects(projectsWithCsvErrors);
        this.loadArchivedProjects();
      }, ((error: any) => {
        this.loadArchivedProjects();
      }));
  }

  private reloadCsvErrorsInCurrentProjects(projectsWithCsvErrors: ProjectOverviewViewModel[]): void {
    projectsWithCsvErrors.forEach(errorProject => {
      let project = this.projects.find(p => p.id === errorProject.id);
      project.maxInvalidRoutesExceeded = errorProject.maxInvalidRoutesExceeded;
      project.invalidCsvFormatLines = errorProject.invalidCsvFormatLines;
      project.failedCsvLines = errorProject.failedCsvLines;
    });
  }

  private loadDraftRoutes(): void {
    const draftRoutes = this.projectOverviewService.getDraftRoutes();
    if (!draftRoutes || draftRoutes.length === 0) { return; }

    draftRoutes.forEach((d: RouteDraft) => {
      const project = this.currentProjects.find((p: ProjectOverviewViewModel) => p.id === d.projectId);
      if (!project) { return; }

      let job = new ProjectJobOverviewViewModel({
        id: d.jobId,
        name: d.name,
        start: d.startDate,
        end: d.endDate,
        schedule: new Schedule({
          startTime: d.startTime,
          endTime: d.endTime,
          intervalMinutes: null,
          days: null
        }),
        archived: false,
        createdDate: null,
        status: null,
        userId: null,
        finishedAt: null,
        fromCsv: true,
        needsCorrection: true
      });

      project.jobs.unshift(job);
    });
  }

  private loadArchivedProjects(): void {
    this.projectOverviewService.getArchivedProjects()
      .subscribe((result: Array<ProjectOverviewViewModel>) => {
        this.archivedProjects = _.filter(result, (project: ProjectOverviewViewModel) => {
          return project.userId === this.userId;
        });
        this.setCurrentProjects();
        this.loadDraftRoutes();
        this.loadingProjects = false;
      }, ((error: any) => {
        this.loadingProjects = false;
      }));
  }

  private setCurrentProjects() {
    switch (this.activeOverviewType) {
      case (OverviewType.Shared): {
        this.currentProjects = this.getCompanyProjects();
        break;
      }
      case (OverviewType.Archived): {
        this.currentProjects = this.archivedProjects;
        break;
      }
      default: {
        this.currentProjects = this.getOwnProjects();
        break;
      }
    }

    this.deactivateSearchbox();
  }

  getOwnProjects(): Array<ProjectOverviewViewModel> {
    return _.filter(this.projects, (project: ProjectOverviewViewModel) => {
      return project.userId === this.userId;
    });
  }

  getCompanyProjects(): Array<ProjectOverviewViewModel> {
    return _.filter(this.projects, (project: ProjectOverviewViewModel) => {
      return project.userId !== this.userId;
    });
  }

  onSaveRoutes(routes: RouteImport[]): void {
    this.joinSaveRoutes(routes, this.selectedProject.id)
      .finally(() => this.loadProjects())
      .subscribe();

    this.closeRouteImport();
  }

  private joinSaveRoutes(routes: RouteImport[], selectedProjectId: string): Observable<void[]> {
    let subscriptions: Observable<void>[] = [];
    _.each(routes, (route: RouteImport) => {
      if (route.added || route.toRemove) {
        subscriptions.push(this.projectOverviewService.addJobToProject(route, selectedProjectId));
      }
    });
    return Observable.forkJoin(subscriptions);
  }

  closeRouteImport(): void {
    this.selectedProject = null;
    this.addedRoutes = [];
    this.availableRoutes = [];
    this.routeImportShown = false;
  }

  addRouteToProject(projectId: string): void {
    this.router.navigate(['auth/new-route', projectId]);
  }

  deleteProject(project: ProjectOverviewViewModel): void {
    this.selectedProject = project;
    this.deleteShown = true;
  }

  onDeleteProject(projectId: string): void {
    this.projectOverviewService.deleteProject(projectId)
      .finally(() => {
        this.closeDeleteProject();
        this.loadProjects();
      })
      .subscribe();
  }

  closeDeleteProject(): void {
    this.selectedProject = null;
    this.deleteShown = false;
  }

  stopJob(job: ProjectJobOverviewViewModel): void {
    this.selectedRoute = job;
    this.stopRouteShown = true;
  }

  deleteJob(job: ProjectJobOverviewViewModel, project: ProjectOverviewViewModel): void {
    this.selectedRoute = job;
    this.selectedProject = project;
    this.deleteRouteShown = true;
  }

  onDeleteRoute(): void {
    if (this.selectedRoute.needsCorrection) {
      this.projectOverviewService.deleteRouteDraft(this.selectedRoute.id);
      this.selectedProject.jobs.splice(this.selectedProject.jobs.indexOf(this.selectedRoute), 1);
      this.selectedRoute = null;
      this.selectedProject = null;
      this.deleteRouteShown = false;
      return;
    }

    this.projectOverviewService.deleteRoute(this.selectedRoute.id)
      .subscribe(() => {
        this.selectedRoute = null;
        this.selectedProject = null;
        this.deleteRouteShown = false;
        this.loadProjects();
      });
  }

  onStopRoute(): void {
    this.projectOverviewService.stopRoute(this.selectedRoute.id)
      .subscribe(() => {
        this.selectedRoute = null;
        this.stopRouteShown = false;
        this.loadProjects();
      });
  }

  cancelStopDeleteRoute(): void {
    this.stopRouteShown = false;
    this.deleteRouteShown = false;
    this.selectedRoute = null;
  }

  isDeleteRouteShown(): boolean {
    return this.deleteRouteShown;
  }
  isStopRouteShown(): boolean {
    return this.stopRouteShown;
  }

  copyRoute(job: ProjectJobOverviewViewModel, project: ProjectOverviewViewModel): void {
    this.router.navigate(['auth/copy-route', job.id, project.id]);
  }

  archiveProject(project: ProjectOverviewViewModel): void {
    this.projectOverviewService.archiveProject(project).subscribe((result: any) => {
      this.loadProjects();
    });
  }

  isSharing(project: ProjectOverviewViewModel): boolean {
    return project.sharingKey !== undefined && project.sharingKey !== null;
  }

  shareProject(project: ProjectOverviewViewModel): void {
    if (project.sharingKey !== undefined && project.sharingKey !== null) {
      this.selectedProject = project;
      this.selectedShareLink = this.projectOverviewService.getSharingLink(project.sharingKey);
      this.sharePopupShown = true;
    } else {
      this.projectOverviewService.shareProject(project).subscribe((result: any) => {
        this.selectedProject = project;
        this.selectedShareLink = result;
        this.sharePopupShown = true;
      });
    }
  }

  onCloseSharePopup(): void {
    this.sharePopupShown = false;
    this.selectedShareLink = '';
  }

  onCloseStopSharingPopup(): void {
    this.stopSharingPopupShown = false;
    this.selectedShareLink = '';
  }

  unshareProject(project: ProjectOverviewViewModel): void {
    this.selectedProject = project;
    this.selectedShareLink = this.projectOverviewService.getSharingLink(project.sharingKey);
    this.stopSharingPopupShown = true;
  }

  stopSharing(project: ProjectOverviewViewModel): void {
    this.projectOverviewService.unshareProject(project).subscribe((result: any) => {
      this.stopSharingPopupShown = false;
    });
  }

  canArchive(project: ProjectOverviewViewModel) {
    if (!project.jobs || project.jobs.length === 0) { return; }

    let result: ProjectJobOverviewViewModel[] = [];
    result = project.jobs.filter((job: ProjectJobOverviewViewModel) => {
      return job.status !== JobStatus.Finished && job.status !== JobStatus.Stopped;
    });
    return result.length === 0;
  }

  openProject(project: Project): void {
    this.router.navigate(['auth/route-detail', project.jobs[0].id, project.id]);
  }

  expand(projectId: string): boolean {
    return _.includes(this.expandedProjects, projectId);
  }

  hasJobs(project: ProjectOverviewViewModel): boolean {
    return project.jobs.length > 0;
  }

  hasValidJobs(project: ProjectOverviewViewModel): boolean {
    return project.jobs.some((j: ProjectJobOverviewViewModel) => !j.needsCorrection);
  }

  routeHasAlerts(routeId: string): boolean {
    let alerts = _.find(this.alerts, (alert: Alert) => {
      return alert.jobId === routeId;
    });
    return alerts !== undefined;
  }

  manageAlerts(route: ProjectJobOverviewViewModel): void {
    this.selectedRoute = route;
    this.selectedProject = this.projects.find(p => p.jobs.includes(route));
    this.alertsShown = true;
  }
  isAlertsShown(): boolean {
    return this.alertsShown;
  }
  closeAlerts(): void {
    this.selectedRoute = undefined;
    this.alertsShown = false;
  }

  toggleExpand(projectId: string) {
    let index = _.indexOf(this.expandedProjects, projectId);
    if (index >= 0) {
      this.expandedProjects.splice(index, 1);
    } else {
      this.expandedProjects.push(projectId);
    }
  }

  isLoading(): boolean {
    return this.loadingProjects || this.readingCsv || this.validatingCsv || this.createInProgress;
  }

  isReadingCsv(): boolean {
    return this.readingCsv;
  }

  isValidatingCsv(): boolean {
    return this.validatingCsv;
  }

  isCreatingRoute(): boolean {
    return this.createInProgress;
  }

  noProjects(): boolean {
    return this.projects === undefined || this.projects.length === 0;
  }

  areSubComponentsShown() {
    return this.createProjectShown || this.routeImportShown ||
      this.deleteShown || this.deleteRouteShown ||
      this.stopRouteShown || this.alertsShown;
  }

  isDeleteShown(): boolean {
    return this.deleteShown;
  }
  isSharePopupShown(): boolean {
    return this.sharePopupShown;
  }
  isStopSharingPopupShown(): boolean {
    return this.stopSharingPopupShown;
  }
  isStopShown(): boolean {
    return this.stopShown;
  }
  isRouteImportShown(): boolean {
    return this.routeImportShown;
  }
  isCreateProjectShown(): boolean {
    return this.createProjectShown;
  }

  isActiveRoute(route: ProjectJobOverviewViewModel): boolean {
    return route.status === JobStatus.Created || route.status === JobStatus.Scheduled;
  }

  containsActiveRoutes(project: ProjectOverviewViewModel): boolean {
    return project.jobs.some((route: ProjectJobOverviewViewModel) => {
      return route.status === JobStatus.Created || route.status === JobStatus.Scheduled;
    });
  }

  routeNeedsCorrection(route: ProjectJobOverviewViewModel): boolean {
    return route.needsCorrection;
  }

  activateMy(): void {
    this.activeOverviewType = OverviewType.Own;
    this.createProjectShown = false;
    this.routeImportShown = false;
    this.deleteShown = false;
    this.setCurrentProjects();
  }

  activateShared(): void {
    this.activeOverviewType = OverviewType.Shared;
    this.createProjectShown = false;
    this.routeImportShown = false;
    this.setCurrentProjects();
  }

  activateArchive(): void {
    this.activeOverviewType = OverviewType.Archived;
    this.createProjectShown = false;
    this.routeImportShown = false;
    this.setCurrentProjects();
  }

  showCreateProject(): void {
    this.createProjectShown = true;
  }

  showRouteImport(project: ProjectOverviewViewModel): void {
    this.selectedProject = project;
    this.addedRoutes = this.orderRoutesAscending(
      this.selectedProject.jobs.map((job: ProjectJobOverviewViewModel) => new RouteImport(job))
    );

    this.projectOverviewService.getOwnRoutes()
      .subscribe((result: RouteImport[]) => {
        this.availableRoutes = this.orderRoutesAscending(result);
      });

    this.routeImportShown = true;
  }

  myProjectsActive(): boolean {
    return this.activeOverviewType === OverviewType.Own;
  }
  sharedProjectsActive(): boolean {
    return this.activeOverviewType === OverviewType.Shared;
  }
  archivedProjectsActive(): boolean {
    return this.activeOverviewType === OverviewType.Archived;
  }

  showProjects(): boolean {
    return !this.isLoading() && !this.areSubComponentsShown() && !this.isStopShown() && !this.isAlertsShown() && !this.isSharePopupShown() && !this.isStopSharingPopupShown();
  }

  createProject(projectName: string): void {
    this.projectOverviewService.createProject(projectName)
      .finally(() => {
        this.activateMy();
        this.loadProjects();
        this.selectedProject = null;
      })
      .subscribe();
  }

  cancelCreateProject(): void {
    this.createProjectShown = false;
    this.selectedProject = null;
  }

  orderProjectsBy(ordering: Ordering): void {
    this.projectOverviewService.setLastOrderMethod(ordering);

    switch (ordering) {
      case Ordering.CreatedDesc:
        this.currentProjects = _.orderBy(this.currentProjects, ['creationDate'], ['desc']);
        break;
      case Ordering.AlphaDesc:
        this.currentProjects = _.orderBy(this.currentProjects, ['name'], ['desc']);
        break;
      default:
        this.currentProjects = _.orderBy(this.currentProjects, ['name'], ['asc']);
        break;
    }
    this.filteredProjects = this.currentProjects;
  }

  private orderRoutesAscending(list: RouteImport[]): RouteImport[] {
    return _.orderBy(list, ['name'], ['asc']);
  }

  activateSearchbox(): void {
    this.searchboxActive = true;
    setTimeout(() => {
      this.searchBox.nativeElement.focus();
    }, 0);
  }

  deactivateSearchbox(): void {
    this.searchTerm = '';
    this.searchboxActive = false;
    this.orderProjectsBy(this.projectOverviewService.getLastOrderMethod());
  }

  isSearchboxActive(): boolean {
    return this.searchboxActive;
  }

  filterRoutes(event?: any): void {
    if (!this.currentProjects) { return; }
    const value = event.target.value;
    if (value.length === 0) {
      this.filteredProjects = this.currentProjects;
      return;
    }

    this.filteredProjects = _.filter(this.currentProjects, (project: ProjectOverviewViewModel) => {
      let hasRoute = _.find(project.jobs, (job: ProjectJobOverviewViewModel) => {
        return job.name.toLocaleLowerCase().indexOf(value.toLocaleLowerCase()) !== -1;
      });
      return project.name.toLocaleLowerCase().indexOf(value.toLocaleLowerCase()) !== -1 || hasRoute !== undefined;
    });
  }

  uploadCsv(event: Event, project: ProjectOverviewViewModel): void {
    let files: Array<File> = event.target['files'];
    if (event.target['files']) {
      this.readFile(files[0], project);
    }
  }

  private readFile(file: File, project: ProjectOverviewViewModel): void {
    this.fileReader.onload = () => {
      this.readingCsv = true;

      const data = this.fileReader.result.toString();
      const lines = data.split('\n');
      project.maxInvalidRoutesExceeded = false;
      project.invalidCsvFormatLines = [];
      project.failedCsvLines = [];

      let propertiesList: string[][] = [];
      for (let i = 0; i < lines.length; i++) {
        const routeString = lines[i];
        let properties = routeString.split(';');

        if (i === lines.length - 1 && routeString.length === 0) {
          continue;
        }
        if (properties.length < 14 || properties.length > 15) {
          project.invalidCsvFormatLines.push(i + 1);
          continue;
        }
        propertiesList.push(properties);
      }

      if (project.invalidCsvFormatLines.length > 0) {
        this.readingCsv = false;
        return;
      }

      this.readingCsv = false;
      this.validateRoutes(propertiesList, project);
    };

    this.fileReader.onloadstart = () => this.readingCsv = true;
    this.fileReader.readAsText(file);
  }

  private async validateRoutes(propertiesList: string[][], project: ProjectOverviewViewModel): Promise<void> {
    this.validatingCsv = true;
    let validRoutes: EditRouteViewModel[] = [];
    let errorRoutes: EditRouteViewModel[] = [];
    this.csvLinesToProcess = propertiesList.length;
    this.maxAllowedErrorRoutes = Math.floor((propertiesList.length / 100) * this.maxRouteUploadDeviationPercent);
    this.csvLinesWithInsufficientQuota = 0;

    for (let i = 0; i < propertiesList.length; i++) {
      this.currentCsvLineNumber = i + 1;
      const properties = propertiesList[i];

      let route = await this.getRoute(properties[7], properties[8], properties[9]);
      let editRouteVm = this.createEditRouteViewModel(properties, route, project.id, i);
      const valid = await this.validateRouteViewModel(editRouteVm);

      if (valid) {
        validRoutes.push(editRouteVm);
      } else {
        errorRoutes.push(editRouteVm);
      }

      if (errorRoutes.length >= 10 && errorRoutes.length >= this.maxAllowedErrorRoutes) {
        project.maxInvalidRoutesExceeded = true;
        this.validatingCsv = false;
        return;
      }

      if (i < propertiesList.length - 1) {
        await sleep(500);
      }
    }

    this.validatingCsv = false;
    await this.createValidatedRoutes(project, validRoutes, errorRoutes);
  }

  private async createValidatedRoutes(project: ProjectOverviewViewModel, validRoutes: EditRouteViewModel[], errorRoutes: EditRouteViewModel[]): Promise<void> {
    this.createInProgress = true;

    if (validRoutes.length > 0) {
      this.csvLinesToProcess = validRoutes.length;

      for (let index = 0; index < validRoutes.length; index++) {
        let editRouteVm = validRoutes[index];
        this.currentCsvLineNumber = index + 1;

        await sleep(100);
        if (!await this.checkRequestQuota(editRouteVm)) {
          this.csvLinesWithInsufficientQuota++;
          errorRoutes.push(editRouteVm);
          continue;
        }

        try {
          let jobId = await this.newRouteService.saveNewRoute(this.mapEditRouteVmToNewRouteVm(editRouteVm)).toPromise();
          if (jobId) {
            const jobCreated = await this.checkCreationProgress(jobId);
            if (!jobCreated) {
              project.failedCsvLines.push(editRouteVm.lineNumber + 1);
            }
          } else {
            project.failedCsvLines.push(editRouteVm.lineNumber + 1);
          }
        } catch (error) {
          project.failedCsvLines.push(editRouteVm.lineNumber + 1);
        }
      };
    }

    if (errorRoutes.length > 0) {
      errorRoutes = _.orderBy(errorRoutes, ['name'], ['desc']);
      this.projectOverviewService.saveDraftRoutes(errorRoutes.map((e: EditRouteViewModel) => new RouteDraft(e)));
      project.jobs = [...this.mapEditRouteVmsToProjectJobVms(errorRoutes), ...project.jobs];
    }

    this.createInProgress = false;
    this.loadProjects();
  }

  private createEditRouteViewModel(properties: string[], route: any, projectId: string, lineNumber: number): EditRouteViewModel {
    let editRouteVm = new EditRouteViewModel({
      jobId: GuidService.GenerateGuid(),
      projectId: projectId,
      name: properties[0],
      startDate: this.parseDate(properties[1]),
      endDate: this.parseDate(properties[2]),
      startTime: this.parseTime(properties[3]),
      endTime: this.parseTime(properties[4]),
      roadsegment: this.parseSectionLength(properties[10]),
      interval: this.parseRequestInterval(properties[5]),
      route: route,
      days: this.parseDays(properties[6]),
      calculateSpectrogram: this.parseNormalHeatmapType(properties[11]),
      calculateBudgetSpectrogram: this.parseLightweightHeatmapType(properties[11]),
      includeHistoricalData: this.parseOption(properties[12]),
      locations: this.parseRoute(route),
      expectedRouteLength: tryParseInt(properties[13]),
      branchingAnalyse: properties.length > 14 ? this.parseOption(properties[14]) : false,
      lineNumber: lineNumber
    });
    return editRouteVm;
  }

  private mapEditRouteVmToNewRouteVm(editRouteVm: EditRouteViewModel): NewRouteViewModel {
    return new NewRouteViewModel({
      name: editRouteVm.name,
      projectId: editRouteVm.projectId,
      startDate: editRouteVm.startDate,
      endDate: editRouteVm.endDate,
      startTime: editRouteVm.startTime,
      endTime: editRouteVm.endTime,
      roadsegment: editRouteVm.roadsegment,
      interval: editRouteVm.interval,
      route: editRouteVm.route,
      days: editRouteVm.days,
      calculateSpectrogram: editRouteVm.calculateSpectrogram,
      calculateBudgetSpectrogram: editRouteVm.calculateBudgetSpectrogram,
      includeHistoricalData: editRouteVm.includeHistoricalData,
      branchingAnalyse: editRouteVm.branchingAnalyse
    });
  }

  private mapEditRouteVmsToProjectJobVms(errorRoutes: EditRouteViewModel[]): ProjectJobOverviewViewModel[] {
    let errorRoutesOverviewVm = [];
    errorRoutes.forEach((e: EditRouteViewModel) => {
      errorRoutesOverviewVm.push(new ProjectJobOverviewViewModel({
        id: e.jobId,
        name: e.name,
        start: e.startDate,
        end: e.endDate,
        schedule: new Schedule({
          startTime: e.startTime,
          endTime: e.endTime,
          intervalMinutes: null,
          days: null
        }),
        archived: false,
        createdDate: null,
        status: null,
        userId: null,
        finishedAt: null,
        fromCsv: true,
        needsCorrection: true
      }));
    });
    return errorRoutesOverviewVm;
  }

  private validateRouteViewModel(editRouteVm: EditRouteViewModel): boolean {
    if (editRouteVm.name === null || editRouteVm.name === undefined || editRouteVm.name.length === 0) { return false; }
    if (!this.datesValid(editRouteVm.startDate, editRouteVm.endDate)) { return false; }
    if (!this.timesValid(editRouteVm.startTime, editRouteVm.endTime)) { return false; }
    if (editRouteVm.interval === null) { return false; }
    if (editRouteVm.days.length === 0) { return false; }
    if (!this.sectionLengthValid(editRouteVm.roadsegment, editRouteVm.calculateSpectrogram)) { return false; }
    return this.routeValid(editRouteVm.route);
  }

  private async checkRequestQuota(editRouteVm: EditRouteViewModel): Promise<boolean> {
    const dates = this.newRouteService.countDays(editRouteVm.startDate, editRouteVm.endDate, editRouteVm.days);
    const startTime = moment(editRouteVm.startTime, 'HH:mm').toDate();
    const endTime = moment(editRouteVm.endTime, 'HH:mm').toDate();

    let snapshots = this.newRouteService.calculateRequestsPerDay(startTime, endTime, editRouteVm.interval);
    let historicDays = this.newRouteService.calculateHistoricalDays(editRouteVm.startDate, editRouteVm.endDate, editRouteVm.days);

    const routeLength = editRouteVm.route.routes[0].legs[0].distance.value;
    if (historicDays > 0 && routeLength > 0 && dates.length > 0) {
      const calculateRequest = new CalculateQuotaRequest(routeLength, dates, editRouteVm.roadsegment, snapshots, historicDays,
        editRouteVm.calculateSpectrogram, editRouteVm.calculateBudgetSpectrogram, editRouteVm.includeHistoricalData, editRouteVm.branchingAnalyse);

      const result: CalculateQuotaResponse = await this.newRouteService.calculateQuota(calculateRequest);
      return !result.quotaInsufficient;
    }
    return false;
  }

  private parseDate(dateString: string): Date {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  }

  private parseTime(timeString: string): string {
    if (moment(timeString, 'HH:mm').isValid()) {
      return timeString;
    }
    return null;
  }

  private parseRequestInterval(intervalString: string): number {
    const possibleIntervals = [1, 2, 5, 10, 15];
    const interval = tryParseInt(intervalString);
    if (interval !== null && possibleIntervals.includes(interval)) { return interval; }
    return null;
  }

  private parseDays(daysString: string): number[] {
    const days = daysString.split(',').map(d => tryParseInt(d));
    if (!days && days.length > 0) { return []; }

    let result: number[] = [];
    const possibleNumbers = [1, 2, 3, 4, 5, 6, 7];
    for (let day of days) {
      if (!possibleNumbers.includes(day)) {
        return [];
      }
      if (day === 7) {
        result.push(0);
      } else {
        result.push(day);
      }
    }

    return result.filter((item, index) => result.indexOf(item) === index);
  }

  private parseSectionLength(roadsegmentString: string): number {
    const roadsegment = tryParseInt(roadsegmentString);
    if (roadsegment === null || roadsegment % 50 > 0 || roadsegment < 50 || roadsegment > 1000) { return null; }
    return roadsegment;
  }

  private parseNormalHeatmapType(value: string): boolean {
    if (value === null || value === undefined || value.length === 0) { return false; }
    if (value.toLowerCase() === 'y') { return true; }
    if (value.toLowerCase() === 'n') { return false; }
    return value === '1';
  }

  private parseLightweightHeatmapType(value: string): boolean {
    if (value === null || value === undefined || value.length === 0) { return false; }
    return value === '2';
  }

  private parseOption(yesNoString: string): boolean {
    if (yesNoString === null || yesNoString === undefined || yesNoString.length === 0) { return false; }
    return yesNoString.toLowerCase().includes('y');
  }

  private parseRoute(route: any): Position[] {
    if (!route || !route.routes || route.routes.length === 0) { return []; }
    return this.newRouteService.mapWaypoints(route);
  }

  private datesValid(startDate: Date, endDate: Date): boolean {
    if (startDate === null || endDate === null) { return false; }

    let today = new Date();
    today.setHours(0, 0, 0, 0);
    return ((startDate && endDate) && (startDate >= today) && (endDate >= startDate));
  }

  private timesValid(startTimeString: string, endTimeString: string): boolean {
    return startTimeString !== null && endTimeString !== null;
  }

  private sectionLengthValid(roadsegment: number, calculateSpectogram: boolean): boolean {
    return !calculateSpectogram || roadsegment !== null;
  }

  private routeValid(route: any): boolean {
    return route !== undefined && route !== null && route.routes && route.routes.length > 0;
  }

  private mapStringToCoordinate(coordinates: string): Coords {
    const coords = coordinates.split(',');
    return new Coords(parseFloat(coords[0]), parseFloat(coords[1]));
  }

  private async getRoute(startAddress: string, endAddress: string, waypointsString: string): Promise<any> {
    const startcoords = this.mapStringToCoordinate(startAddress);
    const endCoords = this.mapStringToCoordinate(endAddress);

    const waypoints = waypointsString.split('|');
    let waypointCoords = [];

    if (waypointsString.length > 0) {
      waypoints.forEach(wp => waypointCoords.push(this.mapStringToCoordinate(wp)));
    }

    const result = await this.mapsService.getRoute(startcoords, endCoords, waypointCoords);
    return result.route;
  }

  hasInvalidRoutes(project: ProjectOverviewViewModel): boolean {
    return this.csvUploadedWithErrors(project) || this.csvUploadFailed(project) || this.hasFailedCsvLines(project);
  }

  exceededMaxInvalidRoutes(project: ProjectOverviewViewModel): boolean {
    return project.maxInvalidRoutesExceeded;
  }

  csvUploadedWithErrors(project: ProjectOverviewViewModel): boolean {
    return project.jobs.some((j: ProjectJobOverviewViewModel) => j.needsCorrection);
  }

  csvUploadFailed(project: ProjectOverviewViewModel): boolean {
    return project.maxInvalidRoutesExceeded || this.hasInvalidCsvFormat(project);
  }

  getNumberOfInvalidRoutes(project: ProjectOverviewViewModel): number {
    return project.jobs.filter((j: ProjectJobOverviewViewModel) => j.needsCorrection).length;
  }

  getFailedCsvLines(project: ProjectOverviewViewModel): string {
    let linesString = '';
    project.failedCsvLines.forEach((line, index) => {
      linesString += line;
      if (index < project.failedCsvLines.length - 1) {
        linesString += ', ';
      }
    });
    return linesString;
  }

  getInvalidCsvFormatLines(project: ProjectOverviewViewModel): string {
    let linesString = '';
    project.invalidCsvFormatLines.forEach((line, index) => {
      linesString += line;
      if (index < project.invalidCsvFormatLines.length - 1) {
        linesString += ', ';
      }
    });
    return linesString;
  }

  hasOneInvalidRoute(project: ProjectOverviewViewModel): boolean {
    return project.jobs.filter((j: ProjectJobOverviewViewModel) => j.needsCorrection).length === 1;
  }

  hasMultipleInvalidRoutes(project: ProjectOverviewViewModel): boolean {
    return project.jobs.filter((j: ProjectJobOverviewViewModel) => j.needsCorrection).length > 1;
  }

  hasInvalidCsvFormat(project: ProjectOverviewViewModel): boolean {
    return project.invalidCsvFormatLines.length > 0;
  }

  hasFailedCsvLines(project: ProjectOverviewViewModel): boolean {
    return project.failedCsvLines.length > 0;
  }

  hasJobName(route: ProjectJobOverviewViewModel): boolean {
    return route.name !== null && route.name !== undefined && route.name.length > 0;
  }

  isCreatedNew(route: ProjectJobOverviewViewModel): boolean {
    return !route.createdDate || ((route.status === JobStatus.Created || route.status === JobStatus.Scheduled) && moment(new Date()).diff(route.createdDate, 'days') <= 5);
  }

  isUploadedRoute(route: ProjectJobOverviewViewModel): boolean {
    return route.fromCsv;
  }

  hasValidFromDate(job: ProjectJobOverviewViewModel): boolean {
    return job.fromDate !== null;
  }

  hasValidToDate(job: ProjectJobOverviewViewModel): boolean {
    return job.toDate !== null;
  }

  hasCsvLinesWithInsufficientQuota(): boolean {
    return this.csvLinesWithInsufficientQuota > 0;
  }

  getCurrentCsvLineNumber(): number {
    return this.currentCsvLineNumber;
  }

  getCsvLinesToProcess(): number {
    return this.csvLinesToProcess;
  }

  getCsvLinesWithInsufficientQuota(): number {
    return this.csvLinesWithInsufficientQuota;
  }

  private async checkCreationProgress(jobId: string): Promise<boolean> {
    for (let i = 0; i < 10; i++) {
      await sleep(3000);
      let status = await this.newRouteService.getJobStatus(jobId).toPromise();
      if (status === JobStatus.Submitted) {
        continue;
      }
      if (status === JobStatus.QuotaInsufficient) {
        return false;
      }
      return true;
    }
    return false;
  }

  editRoute(job: ProjectJobOverviewViewModel): void {
    this.router.navigate(['auth/edit-route', job.id]);
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/project-overview/project-overview.component.ts